import { Bell } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import RegisterPage from './RegisterPage';

import { useVersion } from '../hooks/useVersion';

const LoginPage: React.FC = () => {
  const version = useVersion();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const { login } = useAuth();

  // Version is now handled by useVersion hook

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    if (username.trim().length < 2) {
      setError('Username must be at least 2 characters long');
      return;
    }

    if (!password) {
      setError('Password is required');
      return;
    }

    if (password.length < 3) {
      setError('Password must be at least 3 characters long');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      const result = await login(username.trim(), password, rememberMe);
      if (!result.success) {
        setError(result.message || 'Invalid username or password. Please check your credentials and try again.');
      }
    } catch {
      setError('Login failed. Please check your network connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: 'var(--input-bg)',
    borderColor: 'var(--input-border)',
    color: 'var(--text-primary)',
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--surface)' }}>
      <div className="max-w-sm w-full space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <Bell size={24} className="text-blue-500" />
            <h1 className="text-xl font-extrabold tracking-tight" style={{ color: 'var(--text-primary)' }}>
              Alert Manager
            </h1>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            {showRegister ? 'Create your account' : 'Sign in to continue'}
          </p>
          <p className="text-[10px] mt-1 font-mono" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
            v{version}
          </p>
        </div>

        {showRegister ? (
          <RegisterPage onSwitchToLogin={() => setShowRegister(false)} />
        ) : (
          <div className="rounded-lg p-6 border" style={{ backgroundColor: 'var(--surface-raised)', borderColor: 'var(--border)' }}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                  style={inputStyle}
                  autoComplete="username"
                  autoFocus
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
                  style={inputStyle}
                  autoComplete="current-password"
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                  disabled={isSubmitting}
                />
                <label htmlFor="remember-me" className="ml-2 block text-xs" style={{ color: 'var(--text-muted)' }}>
                  Remember me
                </label>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-2.5">
                  <p className="text-xs text-red-500 font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2 px-4 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowRegister(true)}
                  className="text-xs transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  disabled={isSubmitting}
                >
                  Create an account
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="text-center text-[10px]" style={{ color: 'var(--text-muted)', opacity: 0.5 }}>
          &copy; {new Date().getFullYear()} Alert Manager
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
